<template>
  <div class="boarding-pass-card">
    <!-- HEADER -->
    <div class="bp-header">
      <div class="bp-logo">
        <div class="logo-mark">TS</div>
        <div class="logo-text">
          <span class="brand">TOURPASS</span>
          <span class="tagline">Boarding Pass</span>
        </div>
      </div>
      <div class="bp-booking">
        <span class="label">BOOKING</span>
        <span class="value">#{{ reservationCode }}</span>
      </div>
    </div>

    <!-- CUERPO -->
    <div class="bp-body">
      <div class="bp-main">
        <div class="service-name">{{ service.servicio }}</div>
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">📅 Fecha</span>
            <span class="info-value">{{ formatDate(service.fecha_servicio) }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">⏰ Hora pickup</span>
            <span class="info-value">{{ formatPickupTime(service.hora_pickup) }}</span>
          </div>
          <div class="info-item full">
            <span class="info-label">📍 Punto de encuentro</span>
            <span class="info-value">{{ service.encuentro }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">👥 Pasajeros</span>
            <span class="info-value">{{ service.pasajero }}</span>
          </div>
        </div>
      </div>
      
      <div class="bp-side">
        <div class="qr-wrapper" v-if="qrDataUrl">
          <img :src="qrDataUrl" alt="QR" class="qr-img" />
        </div>
        <div class="bp-code">
          <span class="bp-label">Boarding Pass</span>
          <span class="bp-value">{{ service.bp }}</span>
        </div>
      </div>
    </div>

    <!-- SEPARADOR -->
    <div class="bp-divider">
      <div class="notch left"></div>
      <div class="dashed"></div>
      <div class="notch right"></div>
    </div>

    <!-- FOOTER -->
    <div class="bp-footer">
      <div class="passenger">
        <span class="label">PASAJERO</span>
        <span class="value">{{ passengerName }}</span>
      </div>
      <div class="contact">
        <span class="label">CONTACTO</span>
        <span class="value">{{ passengerEmail || passengerPhone }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import QRCode from 'qrcode';

export default {
  name: 'BoardingPassCard',
  props: {
    service: { type: Object, required: true },
    reservationCode: { type: String, required: true },
    passengerName: { type: String, required: true },
    passengerEmail: { type: String, default: '' },
    passengerPhone: { type: String, default: '' }
  },
  data() {
    return { qrDataUrl: null };
  },
  async mounted() {
    await this.generateQR();
  },
  methods: {
    async generateQR() {
      try {
        this.qrDataUrl = await QRCode.toDataURL(this.reservationCode + '-' + this.service.bp || 'NO-CODE', {
          width: 120,
          margin: 1,
          color: { dark: '#0f172a', light: '#ffffff' }
        });
      } catch (err) {
        console.error('Error QR:', err);
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return '—';
      const date = new Date(dateStr + 'T00:00:00');
      return date.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase();
    },
    formatPickupTime(time) {
      return time ? time.replace(':00', '') : '—';
    }
  }
};
</script>

<style>
/* ⚠️ SIN 'scoped' para que html2canvas lo lea correctamente */
.boarding-pass-card {
  width: 580px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: #0f172a;
  background: #ffffff;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  page-break-inside: avoid;
  margin-bottom: 20px;
}

.bp-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: #0f172a;
  color: #fff;
}

.bp-logo { display: flex; align-items: center; gap: 12px; }
.logo-mark {
  width: 36px; height: 36px; border-radius: 50%;
  background: linear-gradient(135deg, #22d3ee 0%, #a3e635 100%);
  display: flex; align-items: center; justify-content: center;
  font-weight: 800; font-size: 14px; color: #0f172a;
}
.logo-text { display: flex; flex-direction: column; line-height: 1.1; }
.brand { font-weight: 700; font-size: 14px; letter-spacing: 1.5px; color: #fff; }
.tagline { font-size: 9px; color: #94a3b8; letter-spacing: 1px; text-transform: uppercase; margin-top: 2px; }

.bp-booking { text-align: right; display: flex; flex-direction: column; }
.bp-booking .label { font-size: 9px; color: #94a3b8; letter-spacing: 1.5px; text-transform: uppercase; }
.bp-booking .value { font-size: 16px; font-weight: 700; font-family: monospace; color: #22d3ee; margin-top: 2px; }

.bp-body { display: flex; padding: 24px; gap: 24px; }
.bp-main { flex: 1; }
.service-name { font-size: 18px; font-weight: 700; color: #0f172a; margin-bottom: 16px; }

.info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px 20px; }
.info-item { display: flex; flex-direction: column; gap: 4px; }
.info-item.full { grid-column: 1 / -1; }
.info-label { font-size: 10px; font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; }
.info-value { font-size: 14px; font-weight: 600; color: #0f172a; }

.bp-side {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding-left: 24px; border-left: 2px dashed #cbd5e1; min-width: 130px; gap: 12px;
}
.qr-wrapper { padding: 4px; background: #fff; border: 1px solid #e2e8f0; border-radius: 6px; }
.qr-img { width: 100px; height: 100px; display: block; }
.bp-code { text-align: center; display: flex; flex-direction: column; gap: 2px; }
.bp-label { font-size: 9px; color: #64748b; text-transform: uppercase; letter-spacing: 1px; font-weight: 600; }
.bp-value { font-size: 16px; font-weight: 700; color: #0f172a; font-family: monospace; }

.bp-divider {
  position: relative; height: 20px; display: flex; align-items: center;
  background: #f8fafc;
}
.notch {
  position: absolute; width: 20px; height: 20px;
  background: #f1f5f9; border-radius: 50%; top: 50%; transform: translateY(-50%);
}
.notch.left { left: -10px; border-right: 1px solid #e2e8f0; }
.notch.right { right: -10px; border-left: 1px solid #e2e8f0; }
.dashed { flex: 1; height: 0; border-top: 2px dashed #cbd5e1; margin: 0 10px; }

.bp-footer {
  display: flex; justify-content: space-between;
  padding: 16px 24px; gap: 20px; background: #f8fafc; border-top: 1px solid #e2e8f0;
}
.passenger, .contact { display: flex; flex-direction: column; gap: 4px; flex: 1; }
.bp-footer .label { font-size: 9px; color: #64748b; text-transform: uppercase; letter-spacing: 1px; font-weight: 600; }
.bp-footer .value { font-size: 14px; font-weight: 700; color: #0f172a; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
</style>